import * as qs from 'qs';
import { Table, RecordBatchReader } from 'apache-arrow';
//raw text: defines global `Arrow` inside the blob worker, untouched by babel
import arrowBundleSource from '!raw-loader!apache-arrow/Arrow.es5.min.js';
//raw text: babel would inject bundler helper imports the worker scope lacks
//(and rewrites import.meta, so a real webpack worker chunk is off the table)
import layoutTickWorkerSource from '!raw-loader!./layoutTick.worker.js';
import { _API, getViewport, aggregateTableResults, read as readOnMainThread } from './graph';
import { getWorkerBatchNFOs } from '../../components/renderer/util';
import { createLogger } from '@graphistry/common/logger';
import { traceFields } from '@graphistry/common/traceparent';

const logger = createLogger(__filename);

/*
 * Layout tick ingest in a Web Worker: the worker owns the /read fetch loop
 * and the Arrow decode, transferring each record batch's column bytes to
 * the main thread. Main-thread work per batch is a host-cache memcpy; a
 * version mismatch (structural change) ships the raw response back for the
 * existing whole-table aggregation.
 */
let layoutTickWorker = null;
let tickStreamCounter = 0;

const WORKER_BOOT_TIMEOUT_MS = 10000;
let tickWorkerReady = null;
function markTickWorkerFailed(why) {
    logger.error({ why }, 'layout tick worker unavailable: falling back to main-thread reads');
    if (layoutTickWorker) {
        layoutTickWorker.terminate();   //a late boot must not start a second fetch loop
        layoutTickWorker = null;
    }
    tickWorkerReady = Promise.resolve(false);   //failed = promise + null worker
}

function getLayoutTickWorker() {
    if (tickWorkerReady !== null && layoutTickWorker === null) { return null; }
    if (!layoutTickWorker) {
        //the Arrow UMD is a webpack build that can reference process/global
        //(bundlers normally shim them); provide them in the worker scope
        const workerShims = 'var global = self; var process = { env: {} };\n';
        try {
            layoutTickWorker = new Worker(URL.createObjectURL(new Blob(
                [workerShims, arrowBundleSource, '\n;\n', layoutTickWorkerSource],
                { type: 'application/javascript' }
            )));
        } catch (e) {
            markTickWorkerFailed('construction threw: ' + e.message);
            return null;
        }

        const worker = layoutTickWorker;
        tickWorkerReady = new Promise((resolve) => {
            const timer = setTimeout(() => resolve(false), WORKER_BOOT_TIMEOUT_MS);
            const onFirst = (e) => {
                if (!e.data || e.data.type !== 'ready') { return; }
                clearTimeout(timer);
                worker.removeEventListener('message', onFirst);
                resolve(true);
            };
            worker.addEventListener('message', onFirst);
            worker.addEventListener('error', () => resolve(false));
        });
        tickWorkerReady.then((booted) => booted || markTickWorkerFailed(
            'no ready within ' + WORKER_BOOT_TIMEOUT_MS + 'ms (blocked or died booting)'));

        //blob workers fail silently without this: surface script/runtime errors
        worker.onerror = (e) => {
            console.error('layoutTickWorker error', e.message, 'line', e.lineno);
        };
    }
    return layoutTickWorker;
}

/*
 * Worker-backed read().repeat() loop while simulating.
 * Returns { iterator, dispose }: yields streamBatches' per-batch shapes,
 * except nodesUpdate carries prebuilt buffer NFOs instead of an Arrow batch.
 * The request body is re-pushed to the worker as the version/viewport move --
 * a stale version makes every read a multi-MB full-table mismatch dump.
 */
export const readTicksViaWorker = (trail) => (props = {}) => {
    const session = props.session;
    //resolved to an ABSOLUTE url: the blob worker fetches this, and its base
    //url is blob: -- page-relative paths do not resolve there
    const uri = new URL(`${_API}/read?id=${encodeURIComponent(session)}`, self.location.origin).href;
    //bbox-relative fixed-point positions are requested while the quantization
    //step stays under ~half a device pixel: 16-bit (half the payload) zoomed
    //out, 24-bit (6 bytes/row, precision equal to f32's 24-bit mantissa) past
    //that -- in practice wire-f32 only before any bbox is known; the settle
    //read is f32 via the legacy path. Absent/32 means f32 on the server, so
    //FULL reads and legacy clients are untouched.
    let lastBBox = null;   // [top, right, bottom, left], from tick metadata
    const pickPrecision = () => {
        if (!lastBBox) { return 32; }
        const vp = getViewport(props);
        const zoom = Math.max(
            Math.abs(lastBBox[1] - lastBBox[3]) / (Math.abs(vp.right - vp.left) || 1),
            Math.abs(lastBBox[0] - lastBBox[2]) / (Math.abs(vp.top - vp.bottom) || 1));
        //larger dimension: zoom is the max over BOTH axes, so the band must
        //hold on portrait displays too
        const canvasPx = (Math.max(self.innerWidth || 0, self.innerHeight || 0) || 1920) * (self.devicePixelRatio || 1);
        if (zoom <= (0.5 * 0xFFFF) / canvasPx) { return 16; }
        if (zoom <= (0.5 * 0xFFFFFF) / canvasPx) { return 24; }
        return 32;
    };
    const makeBody = (v) => qs.stringify({
        id: session, v,
        precision: pickPrecision(),
        layoutOptions: props.graph.layoutOptions,
        viewport: getViewport(props),
        filtered: true
    });
    //initialize variables for tracking recency
    let prevVersion = +props.graph.version || 0;
    let prevBody = makeBody(prevVersion);

    const worker = getLayoutTickWorker();
    const tickStreamId = ++tickStreamCounter;
    const inbox = [];
    let wake = null;
    let disposed = false;
    const onMessage = ({ data }) => {
        if (data.tickStreamId !== tickStreamId) { return; }
        if (data.type === 'error') {
            logger.warn({ status: data.status, message: data.message, ...traceFields(trail) },
                'readTicksViaWorker: worker fetch error');
            if (!data.fatal) { return; }
            //fatal errors flow to the generator, which throws them into the
            //stream -- withArrowData's catch runs the legacy recovery
        }
        inbox.push(data);
        if (wake) {
            const resolve = wake;
            wake = null;
            resolve();
        }
    };
    //an uncaught worker error can kill its fetch loop with no message ever
    //arriving: rethread it as a fatal stream error so the legacy health
    //check + session recovery run (not sticky -- the next session retries
    //the worker; only boot failure writes the page off)
    const onWorkerError = (e) => {
        inbox.push({ type: 'error', fatal: true,
            message: 'worker uncaught error: ' + ((e && e.message) || 'unknown') });
        if (wake) {
            const resolve = wake;
            wake = null;
            resolve();
        }
    };
    //explicit teardown, run from the subscriber's finally: the generator
    //parks between ticks awaiting a worker message, and its own finally can
    //only run once one arrives -- an idle worker would otherwise keep the
    //fetch loop, this listener, and the props closure alive indefinitely
    //for a dead subscription
    const dispose = () => {
        if (disposed) { return; }
        disposed = true;
        if (worker) {
            worker.removeEventListener('message', onMessage);
            worker.removeEventListener('error', onWorkerError);
            worker.postMessage({ cmd: 'stop', tickStreamId });
        }
        if (wake) {
            const resolve = wake;
            wake = null;
            resolve();   //unpark the generator so it exits and gets collected
        }
    };
    if (worker) {
        worker.addEventListener('message', onMessage);
        worker.addEventListener('error', onWorkerError);
        worker.postMessage({ cmd: 'start', tickStreamId, uri, v: prevVersion, body: prevBody, traceparent: trail });
    }

    async function* readTicks() {
        let sawBatches = false;
        let lastBoundBox;
        let lastTableLength = 0;
        try {
            //never null here: getLayoutTickWorker made a boot promise (which
            //marks its own failure), or a past failure left a resolved false
            if (!(await tickWorkerReady)) {
                //degraded mode - read on main thread
                while (!disposed) {
                    yield* readOnMainThread(trail)(props);
                }
                return;
            }
            while (!disposed) {
                if (inbox.length === 0) {
                    await new Promise((resolve) => { wake = resolve; });
                }
                const data = inbox.shift();
                if (!data) {
                    continue;   //woken by dispose: loop condition exits
                }
                if (data.type === 'error') {
                    //only fatal errors reach the inbox (repeated fetch
                    //failures): erroring the stream restores the legacy
                    //blast radius -- health check + session recovery
                    throw new Error('readTicksViaWorker: tick stream failed: ' +
                        (data.status != null ? 'HTTP ' + data.status : data.message));
                }
                if (data.type === 'batch') {
                    let boundBox;
                    try { boundBox = data.boundBox && JSON.parse(data.boundBox); } catch (e) {}
                    sawBatches = true;
                    lastBoundBox = boundBox || lastBoundBox;
                    lastBBox = lastBoundBox || lastBBox;   //feeds pickPrecision
                    lastTableLength = data.tableLength;
                    yield {
                        ...props,
                        session,
                        version: data.version,
                        globalBoundBox: boundBox || undefined,
                        nodesTableLength: data.tableLength,
                        nodesUpdate: {
                            offset: data.offset,
                            nfos: getWorkerBatchNFOs('node', data.columns, data.offset)
                        }
                    };
                } else if (data.type === 'tickEnd' && sawBatches) {
                    //per-tick summary for the React path: batch emissions are
                    //consumed by the tick sink, but the camera auto-center
                    //(globalBoundBox) and table lengths still flow as props --
                    //one React cycle per tick instead of one per batch
                    sawBatches = false;
                    yield {
                        ...props,
                        session,
                        globalBoundBox: lastBoundBox,
                        nodesTableLength: lastTableLength
                    };
                } else if (data.type === 'mismatch') {
                    //structural change: full node+edge tables -- the existing
                    //main-thread aggregation, then adopt the learned version so
                    //the body refresh below drops reads back to the fast path
                    const tables = [];
                    for (const reader of await RecordBatchReader.readAll(new Uint8Array(data.buf))) {
                        const batches = [];
                        for await (const batch of reader) {
                            batches.push(batch);
                        }
                        tables.push(new Table(batches));
                    }
                    const result = ['node', 'edge'].reduce((result, name, i) => {
                        try {
                            if (tables[i]) {
                                return aggregateTableResults(result, name, tables[i]);
                            }
                        } catch (e) {}
                        return result;
                    }, {});
                    if (result.version != null) {
                        props.graph.version = result.version;
                    }
                    yield { ...props, ...result };
                }
                const version = +props.graph.version || 0;
                const body = makeBody(version);
                if (body !== prevBody) {
                    prevBody = body;
                    prevVersion = version;
                    worker.postMessage({ cmd: 'updateBody', tickStreamId, v: version, body });
                }
                if (data.type === 'tickEnd') {
                    //flow control: the worker holds the next tick (freshest
                    //wins) until the previous one is fully consumed here.
                    //Acked AFTER the body refresh so the follow-up fetch
                    //carries the just-learned version.
                    worker.postMessage({ cmd: 'ack', tickStreamId });
                }
            }
        } finally {
            dispose();   //backup: the subscriber's finally is the primary path
        }
    }
    return { iterator: readTicks(), dispose };
};
